import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Avatar } from '@node-space/ui-components'
import { Customer, CustomerStatus } from 'types/customers'
import { SourceWallet } from 'types/move/move'
import { TransferWalletsReponse, WalletTransferType } from 'types/unifiedPayouts/unifiedPayouts'
import { formatString, roundNumberWithCommas } from 'utils/utils'

interface UseFormOptionsProps {
  customerList: Customer[]
  transferWallets: TransferWalletsReponse[]
  sourceWallet: SourceWallet
}

export const useFormOptions = ({
  customerList,
  sourceWallet,
  transferWallets,
}: UseFormOptionsProps) => {
  const { t } = useTranslation()

  const customerOptions = useMemo(() => {
    return customerList
      ?.filter?.(customer => customer?.status === CustomerStatus?.VERIFIED)
      ?.map?.(customer => ({
        value: customer?.reference,
        leftText: customer?.name,
      }))
  }, [customerList])

  const transferWalletOptions = useMemo(() => {
    if (!transferWallets) return []

    return transferWallets.map(wallet => {
      const code = wallet?.balance?.currency?.toLowerCase()
      const approximateBalance = roundNumberWithCommas(wallet?.balance?.value, 2)

      return {
        LeftElement: <Avatar name={code} size="small" />,
        leftText: wallet?.name,
        rightText: `${approximateBalance} ${code?.toUpperCase()}`,
        value: wallet?.walletId,
      }
    })
  }, [transferWallets])

  const selectedSourceWalletOption = useMemo(() => {
    if (!sourceWallet) return []

    return [
      {
        LeftElement: <Avatar name={sourceWallet.currency?.toLowerCase()} size="small" />,
        leftText: sourceWallet.walletName,
        rightText: formatString(
          t('balanceWithCurrency'),
          roundNumberWithCommas(sourceWallet.balance, 2),
          sourceWallet.currency
        ),
        value: sourceWallet.walletId,
      },
    ]
  }, [sourceWallet, t])

  const walletTransferTypeOptions = [
    {
      label: t('customers.toAnotherWallet'),
      value: WalletTransferType.MAIN,
    },
    {
      label: t('customers.toACustomerWallet'),
      value: WalletTransferType.CUSTOMER,
    },
  ]
  return {
    customerOptions,
    transferWalletOptions,
    walletTransferTypeOptions,
    selectedSourceWalletOption,
  }
}
